import { HTTP_INTERCEPTORS, provideHttpClient, withFetch, withInterceptorsFromDi } from "@angular/common/http";
import { APP_INITIALIZER, FactoryProvider, ModuleWithProviders, NgModule } from "@angular/core";
import { PlatformService, TelemetryService, WindowRef } from "@obg/ngx.global.evo";
import { isFunction } from "lodash-es";

import { BrowserEventsService, NoopBrowserEventsService } from "./browser-events/browser-events.service";
import { _GLOBAL_CONFIG, GLOBAL_CONFIG } from "./const";
import { LazyInterceptorService } from "./http/http-interceptor.service";
import { I18N_PIPES } from "./i18n/i18n.const";
import { BrowserLocationService, LocationService } from "./location/location.service";
import { GlobalConfig } from "./model";
import { ClientReferrerService, ReferrerService } from "./referrer/referrer.service";
import { SensitiveDataDirective } from "./sensitive-data/sensitive-data.directive";
import { fromMinutesToMilliseconds } from "./utils/conversions/time-conversion";
import { clearMemoizationStore } from "./utils/redux/memoization-store";
import { UTIL_PIPES } from "./utils/utils.const";

const GLOBAL_PIPES = [...I18N_PIPES, ...UTIL_PIPES];

@NgModule({
	declarations: [...GLOBAL_PIPES, SensitiveDataDirective],
	exports: [...GLOBAL_PIPES, SensitiveDataDirective],
})
export class GlobalModule {
	/**
	 * Call `GlobalModule.forRoot` in your application module.
	 *
	 * @param config Config is required, specify it in `forRoot` or alternatively using `GLOBAL_CONFIG` token to utilize DI.
	 */
	static forRoot(config?: GlobalConfig | (() => GlobalConfig)): ModuleWithProviders<GlobalModule> {
		return {
			ngModule: GlobalModule,
			providers: [
				provideHttpClient(withFetch(), withInterceptorsFromDi()),
				{
					provide: BrowserEventsService,
					useFactory: _browserEventsServiceFactory,
					deps: [PlatformService, WindowRef],
				},
				{ provide: ReferrerService, useClass: ClientReferrerService },
				{ provide: GLOBAL_CONFIG, useFactory: _globalConfigFactory, deps: [_GLOBAL_CONFIG] },
				{ provide: LocationService, useClass: BrowserLocationService },
				{ provide: _GLOBAL_CONFIG, useValue: config },
				{
					provide: APP_INITIALIZER,
					multi: true,
					useFactory: _initializeGlobalFactory,
					deps: [PlatformService, TelemetryService, GLOBAL_CONFIG],
				},
				LazyInterceptorService,
				{
					provide: HTTP_INTERCEPTORS,
					useExisting: LazyInterceptorService,
					multi: true,
				},
			],
		};
	}
}

/**
 * @internal
 */
export function _globalConfigFactory(config?: GlobalConfig | (() => GlobalConfig)) {
	return isFunction(config) ? config() : config;
}

/**
 * @internal
 */
export function _browserEventsServiceFactory(
	platformService: PlatformService,
	windowRef: WindowRef
): BrowserEventsService | NoopBrowserEventsService {
	return platformService.isBrowser ? new BrowserEventsService(windowRef) : new NoopBrowserEventsService();
}

/**
 * @internal
 */
export function _initializeGlobalFactory(
	platformService: PlatformService,
	telemetryService: TelemetryService,
	config: GlobalConfig | undefined
): FactoryProvider {
	if (!config) {
		throw new Error("GlobalModule::provide required `GlobalConfig`");
	}

	return _initializeGlobal.bind(null, platformService, telemetryService, config);
}

/**
 * @internal
 */
export function _initializeGlobal(
	platformService: PlatformService,
	telemetryService: TelemetryService,
	config: GlobalConfig
): void {
	if (!globalThis.isDOMSSR && platformService.isBrowser && !config.disableMemoizationClear) {
		/**
		 * Clears memoization store every 5 min fix possible memory leak for longer sessions
		 */
		setInterval(clearMemoizationStore, fromMinutesToMilliseconds(5));
	}

	if (config.initialTelemetryData) {
		telemetryService.track(config.initialTelemetryData);
	}
}
